import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { setTrackAsVerified } from '@alltrails/tracks';
import Button from '@alltrails/denali/components/Button';
import logError from '@alltrails/core/utils/logError';
import FeedbackConfirmationDialog from '@alltrails/layout/components/FeedbackConfirmationDialog';

type TrackAsVerifiedRouteStatus = 'Success' | 'Failed' | 'Setting' | null;

const SuggestAsRouteButton = ({ track, trailId, isAdmin, isPendingOwner }) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [settingTrackAsVerifiedRoute, setSettingTrackAsVerifiedRoute] = useState<TrackAsVerifiedRouteStatus>(null);
  const languageRegionCode = useLanguageRegionCode();
  const isAdminOrPendingOwner = isAdmin || isPendingOwner;
  const [isSettingRoute, setIsSettingRoute] = useState(false);

  const onSetTrackAsVerifiedRoute = e => {
    e.preventDefault();

    if (settingTrackAsVerifiedRoute === 'Setting') {
      return;
    }

    if (isAdminOrPendingOwner && !window.confirm('Use this recording as the verified route?')) {
      return;
    }

    if (isAdmin) {
      setIsSettingRoute(true);
    }

    setSettingTrackAsVerifiedRoute('Setting');
    setTrackAsVerified(track, trailId)
      .then(() => {
        if (isAdmin) {
          setIsSettingRoute(false);
          window.location.href = wrapUrlSafe(`/contribute/route?trail_id=${trailId}`, languageRegionCode);
        }
        setIsConfirmationModalOpen(true);
        setSettingTrackAsVerifiedRoute('Success');
      })
      .catch(error => {
        if (isAdmin) {
          setIsSettingRoute(false);
        }
        setSettingTrackAsVerifiedRoute('Failed');
        logError(new Error(`Error when suggesting as route for trailId: ${trailId}, trackId: ${track.id}, error: ${error.message}`));
      });
  };

  if (isAdminOrPendingOwner) {
    return (
      <Button
        onClick={onSetTrackAsVerifiedRoute}
        size="sm"
        loading={isSettingRoute}
        testId="set-as-verified-route"
        text={<FormattedMessage defaultMessage="Set as route" />}
        variant="default"
      />
    );
  }

  return (
    <>
      <Button
        onClick={onSetTrackAsVerifiedRoute}
        size="sm"
        testId="suggest-as-verified-route"
        text={<FormattedMessage defaultMessage="Suggest as route" />}
        variant="default"
        loading={settingTrackAsVerifiedRoute === 'Setting'}
      />
      <FeedbackConfirmationDialog durationMs={2000} isOpen={isConfirmationModalOpen} onRequestClose={() => setIsConfirmationModalOpen(false)} />
    </>
  );
};

export default SuggestAsRouteButton;
